import { Injectable, Injector } from '@angular/core';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import {
  flatTreeDataByHasChildren,
  generateObjectId,
  getSingleExportFileName,
  getDefaultMeasure,
} from '@app/shared/utils';

import {
  CELL_FILTER,
  CELL_RENDERER,
  ENTITY_TYPES,
  IBaseEntityType,
  IDocumentOperationOptions,
  IXVIListColumn,
  IEntityConfig,
  IOrderInventory,
  IGridFlatInventoryItemVenue,
  SiteForTransfer,
  NotificationType,
  IInventoryItemVenue,
  MEASURE_TRANSACTION,
} from '@app/shared/types';

import { BaseService } from '../base';
import {
  eq,
  filter,
  includes,
  isEqual,
  find,
  flow,
  get,
  has,
  map,
  set,
  isNil,
  sortBy,
  merge,
  first,
  some,
  assign,
  toLower,
} from 'lodash';
import { COLUMN_SELECTOR_ID } from '@geniuspos/ui-shared';
import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import {
  ORDER_FILTER_STATUS_OPTIONS,
  ORDER_STATUS_ENUM_OPTIONS,
  ORDER_STATUS_VALUE,
} from '@app/shared/types/enums/order-status.enum';
import { COMMON_FORM_PATH, TOASTER_ERROR_MESSAGE } from '@app/shared/constants';
import { COLUMN_ALIGN_RIGHT } from '@shared/constants/column-align-definition';
import { lazyInject } from '@app/containers/base/decorators';
import { OrderPdfService } from './order.pdf.service';
import { SitesService } from '../sites/sites.service';
import { markInventoryItemIsUpdated } from '@shared/utils/inventory-item.utils';
import { UiActions } from '@app/store/actions';
import { PdfBaseService } from '@app/shared/common/make-pdf';
import { DecimalNumber } from '@app/shared/common';
import { PdfGenerateServiceV2 } from '@app/shared/common/make-pdf/v2';
import { buildOrderPdfConfig } from './order-pdf-config';
import { ORDER_FORM_FIELD_PATHS } from './order-form-field-paths';
import { OrderTotalFormService } from './order-totals-form.service';
import { StateService } from '../state-service/state.service';
import { InventoryItemVenueService } from '@shared/services/inventory-item-venue';
import { findCurrentCost } from '@shared/services/orders/order.util';
import { I18n } from '@app/i18n/strings-keys';

const entityType: ENTITY_TYPES = ENTITY_TYPES.ORDER;
const config: IEntityConfig = {
  ENTITY_TYPE: entityType,
  isMasterListAvailable: true,
  apiEndpoint: entityType,
  uiRoute: entityType,
  isFormServiceSupported: true,
  needConvertTimeZoneFields: ['transaction_date', 'delivery_date'],
  list: {
    gridOptions: {
      valueField: '_id',
    },
    documentQuery: {
      orderby: 'transaction_date desc',
      notNeedActive: true,
    },
    documentOperationOptions: {
      skipMappers: true,
    },
    columns: [],
  },
  buttons: {
    remove: false,
    actions: false,
    more: false,
  },
};

/**
 * Inventory Item service that extends BaseService to use
 * basic CRUD request operations
 */
@Injectable()
export class OrdersService extends BaseService {
  public static config: IEntityConfig = config;
  public static ENTITY_TYPE: string = config.ENTITY_TYPE;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(OrderPdfService) public orderPdfService: OrderPdfService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(PdfBaseService) public pdfBaseService: PdfBaseService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(PdfGenerateServiceV2)
  public pdfGenerateServiceV2: PdfGenerateServiceV2;
  @lazyInject(OrderTotalFormService)
  public totalFromService: OrderTotalFormService;

  public needGetUpdateItemsStatus: ORDER_STATUS_VALUE[] = [
    ORDER_STATUS_VALUE.Open,
  ];
  public printAndExportMaxLength: number = 50;
  public sites: SiteForTransfer[];

  public headerNames: ColDef[] = [
    {
      headerName: 'Vendor',
      field: 'name',
      filter: false,
    },
    {
      headerName: 'Order Date',
      field: 'order_source_id',
      filter: false,
    },
  ];

  public constructor(public injector: Injector) {
    super(injector);
    this.config.buttons = {
      edit: {
        hidden: true,
      },
      bulk_print: {
        caption: I18n.Common.Print,
        hidden(selectedItems: IBaseEntityType[]): boolean {
          return selectedItems.length < 1;
        },
        click: this.bulkGenerateOrderSummary(false),
      },
      bulk_export: {
        caption: I18n.Common.ExportPDF,
        hidden(selectedItems: IBaseEntityType[]): boolean {
          return selectedItems.length < 1;
        },
        click: this.bulkGenerateOrderSummary(true),
      },
    };

    const listColumns: IXVIListColumn[] = [
      {
        path: 'internal_number',
        title: I18n.Forms.OrderColumns.PurchaseOrderNo,
        filterParams: {
          quickFilter: true,
        },
        sortable: true,
        defaultView: true,
      },
      {
        path: 'vendor_name',
        title: I18n.EntityNames.Vendor,
        filterType: CELL_FILTER.string,
        filterParams: {
          quickFilter: true,
        },
        sortable: true,
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType,
        },
        defaultView: true,
      },
      {
        path: 'transaction_date',
        title: I18n.Forms.OrderColumns.OrderDate,
        filterType: CELL_FILTER.dateLocale,
        sortable: true,
        defaultView: true,
      },
      {
        path: 'delivery_date',
        title: I18n.Common.DeliveryDate,
        filterType: CELL_FILTER.dateLocale,
        sortable: true,
        defaultView: true,
      },
      {
        path: 'total_cost',
        title: I18n.Common.GrandTotal,
        sortable: true,
        defaultView: true,
        valueFormatter: (params: ValueFormatterParams): string => {
          return this.numberFormatService.getNumberFormatter(true)(params);
        },
        ...COLUMN_ALIGN_RIGHT,
      },
      {
        path: 'external_order_number',
        title: I18n.Forms.OrderColumns.ExternalRefNo,
        sortable: true,
        filterParams: {
          quickFilter: true,
        },
        defaultView: true,
      },
      {
        path: 'invoice_number',
        title: I18n.Forms.OrderColumns.InvoiceNo,
        sortable: true,
        filterParams: {
          quickFilter: true,
        },
        defaultView: true,
      },
      {
        path: 'status',
        title: I18n.Common.Status,
        filterType: CELL_FILTER.enum,
        filterParams: {
          values: ORDER_FILTER_STATUS_OPTIONS,
        },
        sortable: true,
        cellRenderer: CELL_RENDERER.prepared_string_renderer,
        cellRendererParams: {
          valueToEnumMap: ORDER_STATUS_ENUM_OPTIONS,
        },
        defaultView: true,
      },
      {
        path: COLUMN_SELECTOR_ID,
        width: 50,
        title: '',
        defaultView: true,
        cellRenderer: CELL_RENDERER.order_actions_renderer,
        pinned: detectMobile() ? false : 'right',
        lockPinned: true,
      },
    ];

    this.config.list.columns.push(...listColumns);
  }

  public processOpenStatusInventoryItemVenues(
    doc: IOrderInventory,
  ): IOrderInventory {
    if (!has(doc, 'inventory_items')) return doc;
    if (!eq(ORDER_STATUS_VALUE.Open, doc.status)) {
      return doc;
    }
    const flattenInventoryItemVenues = map(
      flatTreeDataByHasChildren(get(doc, 'inventory_items', [])),
      (item: IGridFlatInventoryItemVenue) => {
        const selectedMeasure = find(item.measures, (measure) =>
          isEqual(measure.entity_id, item.measure_entity_id),
        );

        return {
          _id: isEqual(get(doc, 'status'), ORDER_STATUS_VALUE.New)
            ? generateObjectId()
            : get(item, '_id'),
          count: parseFloat(`${get(item, 'count', '0.00')}`),
          gl_account_entity_id: get(item, 'gl_account.entity_id'),
          gl_account_name: get(item, 'gl_account.name'),
          item_entity_id: get(item, 'entity_id'),
          item_name: get(item, 'name'),
          major_category_entity_id: get(
            item,
            COMMON_FORM_PATH.reportingCategoryMajorEntityId,
          ),
          major_category_name: get(
            item,
            COMMON_FORM_PATH.reportingCategoryMajorName,
          ),
          major_category_external_id: get(
            item,
            COMMON_FORM_PATH.reportingCategoryMajorExCategoryId,
            undefined,
          ),
          minor_category_entity_id: get(
            item,
            COMMON_FORM_PATH.reportingCategoryMinorEntityId,
          ),
          minor_category_name: get(
            item,
            COMMON_FORM_PATH.reportingCategoryMinorName,
          ),
          minor_category_external_id: get(
            item,
            COMMON_FORM_PATH.reportingCategoryMinorExCategoryId,
            undefined,
          ),
          measure_code: get(selectedMeasure, 'code'),
          measure_description: get(selectedMeasure, 'description'),
          measure_entity_id: get(selectedMeasure, 'entity_id'),
          measure_factor_value: get(selectedMeasure, 'factor_value'),
          measure_id: get(selectedMeasure, 'id'),
        };
      },
    );
    set(
      doc,
      'inventory_items',
      filter(
        flattenInventoryItemVenues,
        (item: IGridFlatInventoryItemVenue) => item.count,
      ),
    );

    return doc;
  }

  public processOtherStatusInventoryItemVenues(
    doc: IOrderInventory,
  ): IBaseEntityType {
    if (!has(doc, 'inventory_items')) return doc;
    if (
      !includes(
        [
          ORDER_STATUS_VALUE.Canceled,
          ORDER_STATUS_VALUE.Pending,
          ORDER_STATUS_VALUE.Completed,
          ORDER_STATUS_VALUE.Rejected,
        ],
        doc.status,
      )
    ) {
      return doc;
    }
    if (
      includes(
        [
          ORDER_STATUS_VALUE.Pending,
          ORDER_STATUS_VALUE.Completed,
          ORDER_STATUS_VALUE.Rejected,
        ],
        doc.status,
      )
    ) {
      const flattenInventoryItemVenues = map(
        flatTreeDataByHasChildren(get(doc, 'inventory_items', [])),
        (item: IGridFlatInventoryItemVenue) => {
          const docResult: IGridFlatInventoryItemVenue = {
            ...item,
            item_cost: new DecimalNumber(
              !isNil(item.item_cost) ? String(item.item_cost) : '0.00',
            ),
            count: new DecimalNumber(
              !isNil(item.count) ? String(item.count) : '0.00',
            ),
          };
          delete docResult['isLeaf'];
          delete docResult['internal_grid_row_id'];
          delete docResult['total_cost'];
          delete docResult['base_measure_count'];
          delete docResult['initial_count'];
          delete docResult['initial_item_cost'];
          delete docResult['initial_total_cost'];
          delete docResult['initial_base_measure_count'];
          delete docResult['majorCategory'];
          delete docResult['minorCategory'];
          delete docResult['reporting_category_major'];
          delete docResult['reporting_category_minor'];
          delete docResult['upc_codes'];

          return docResult;
        },
      );
      set(doc, 'inventory_items', flattenInventoryItemVenues);

      return doc;
    }

    return {
      _id: doc._id,
      status: doc.status,
    };
  }

  public backToFrontItemMapper(doc: IOrderInventory): IBaseEntityType {
    set(doc, 'virtual.inventory_items', get(doc, 'inventory_items'));
    delete doc.inventory_items;

    return flow(
      this.markDocInventoryItemUpdated.bind(this),
      super.backToFrontItemMapper.bind(this),
    )(doc);
  }

  public markDocInventoryItemUpdated(doc: IBaseEntityType): IBaseEntityType {
    if (isEqual(doc.status, ORDER_STATUS_VALUE.Open)) {
      const updatedItems = get(doc, 'updated_items');
      set(
        doc,
        'virtual.inventory_items',
        markInventoryItemIsUpdated(doc.virtual.inventory_items, updatedItems),
      );
    }

    return doc;
  }

  public processDeliveryFeeAndTaxFee(doc: IOrderInventory): IBaseEntityType {
    if (has(doc, 'delivery_fee') && isNil(get(doc, 'delivery_fee'))) {
      set(doc, 'delivery_fee', 0);
    }

    if (has(doc, 'tax_fee') && isNil(get(doc, 'tax_fee'))) {
      set(doc, 'tax_fee', 0);
    }

    return doc;
  }

  public frontToBackItemMapper(doc: IOrderInventory): IOrderInventory {
    if (doc.status === ORDER_STATUS_VALUE.New) {
      doc.status = ORDER_STATUS_VALUE.Open;
    }

    return flow<
      IOrderInventory[],
      IOrderInventory,
      IOrderInventory,
      IOrderInventory,
      IOrderInventory,
      IOrderInventory,
      IOrderInventory
    >(
      this.processOpenStatusInventoryItemVenues.bind(this),
      this.processDeliveryFeeAndTaxFee.bind(this),
      this.processOtherStatusInventoryItemVenues.bind(this),
      this.trimFieldValue.bind(this, 'external_order_number'),
      this.trimFieldValue.bind(this, 'invoice_number'),
      super.frontToBackItemMapper.bind(this),
    )(doc);
  }

  public async fetchGetDocumentById(
    documentId: string,
    options: IDocumentOperationOptions = {},
  ): Promise<IOrderInventory> {
    return this.apiActions.fetchGetDocumentById(entityType, documentId, {
      ...options,
      query: {
        get_updated_items: true,
        ...options.query,
      },
    });
  }

  public async getOpenOrderPdfData(
    currentDocument: IOrderInventory,
    virtualInventoryItemVenues: IInventoryItemVenue[],
  ): Promise<IInventoryItemVenue[]> {
    const vendorEntityId = get(
      currentDocument,
      ORDER_FORM_FIELD_PATHS.vendorEntityId,
    );
    const inventoryItemVenueResponse = await this.inventoryItemVenueService
      .fetchCurrentList(
        {
          filter: `vendors/entity_id eq in(${vendorEntityId}) and measures/transactions/delivery eq in(true)`,
        },
        [currentDocument?.[ORDER_FORM_FIELD_PATHS.siteId]],
      )
      .toPromise();
    const inventoryItemVenueList: IInventoryItemVenue[] = get(
      inventoryItemVenueResponse,
      'items',
      [],
    );

    return map(inventoryItemVenueList, (item) => {
      const foundItem = find(virtualInventoryItemVenues, {
        item_entity_id: item.entity_id,
      });
      if (foundItem && has(foundItem, 'item_name')) {
        return assign({}, item, foundItem);
      }
      const vendor = find(item.vendors, { entity_id: vendorEntityId });
      const sortedVendorMeasures = sortBy(vendor.measures, (measureItem) =>
        toLower(measureItem.description),
      );

      const vendorMeasure = getDefaultMeasure(
        sortedVendorMeasures,
        MEASURE_TRANSACTION.Order,
      );

      return assign({}, item, {
        major_category_entity_id: get(
          item,
          COMMON_FORM_PATH.reportingCategoryMajorEntityId,
        ),
        major_category_name: get(
          item,
          COMMON_FORM_PATH.reportingCategoryMajorName,
        ),
        minor_category_entity_id: get(
          item,
          COMMON_FORM_PATH.reportingCategoryMinorEntityId,
        ),
        minor_category_name: get(
          item,
          COMMON_FORM_PATH.reportingCategoryMinorName,
        ),
        measure_entity_id: vendorMeasure.entity_id,
        gl_account_name: get(item, 'gl_account.name', ''),
        item_cost: findCurrentCost(
          assign({}, item, { measure_entity_id: vendorMeasure.entity_id }),
          vendorEntityId,
        ),
      });
    });
  }

  public async getPdfData(order: IBaseEntityType): Promise<IBaseEntityType> {
    const orderStatus = get(order, 'status', ORDER_STATUS_VALUE.Open);
    const currentDocument: IOrderInventory = await this.fetchGetDocumentById(
      order._id,
      {
        noLoader: true,
        query: {
          get_updated_items: includes(
            this.needGetUpdateItemsStatus,
            orderStatus,
          ),
        },
        suppressErrors: true,
      },
    );

    if (has(currentDocument, 'errors')) {
      return Promise.reject(
        this.i18nService.instant(I18n.ErrorMessages.CheckInternetAndTryAgain),
      );
    }

    const virtualInventoryItemVenues: IInventoryItemVenue[] = get(
      currentDocument,
      ORDER_FORM_FIELD_PATHS.virtualInventoryItemVenues,
      [],
    );

    const inventoryItemVenues: IInventoryItemVenue[] = isEqual(
      orderStatus,
      ORDER_STATUS_VALUE.Open,
    )
      ? await this.getOpenOrderPdfData(
          currentDocument,
          virtualInventoryItemVenues,
        )
      : virtualInventoryItemVenues;

    set(
      currentDocument,
      ORDER_FORM_FIELD_PATHS.inventoryItemVenues,
      inventoryItemVenues,
    );

    return this.orderPdfService.getPdfData(
      merge({}, currentDocument, order),
      null,
      false,
    );
  }

  protected getPayloadBlackList(): string[] {
    return [
      'vendor_name',
      'inventory_items.[each].gl_account_entity_id',
      'inventory_items.[each].gl_account_name',
      'inventory_items.[each].item_name',
      'inventory_items.[each].major_category_entity_id',
      'inventory_items.[each].major_category_name',
      'inventory_items.[each].measure_code',
      'inventory_items.[each].measure_description',
      'inventory_items.[each].measure_factor_value',
      'inventory_items.[each].measure_id',
      'inventory_items.[each].minor_category_entity_id',
      'inventory_items.[each].minor_category_name',
      'inventory_items.[each].minor_category_external_id',
      'inventory_items.[each].major_category_external_id',
    ];
  }

  private bulkGenerateOrderSummary = (isExport: boolean = false): Function => {
    return async (selectedItems: IBaseEntityType[]): Promise<void> => {
      if (selectedItems.length > this.printAndExportMaxLength) {
        this.uiActions.showToasterMessage({
          type: NotificationType.ERROR,
          title: TOASTER_ERROR_MESSAGE.title,
          body: this.i18nService.instant(
            I18n.ErrorMessages.YouCanOnlyOperateUpToMaxCountRecords,
            {
              operate: this.i18nService.instant(
                isExport ? I18n.Common.export : I18n.Common.print,
              ),
              maxCount: this.printAndExportMaxLength,
            },
          ),
        });

        return;
      }

      const isShowAllColumns = some(selectedItems, (selectedItem) => {
        return !includes(
          [ORDER_STATUS_VALUE.Canceled, ORDER_STATUS_VALUE.Open],
          selectedItem.status,
        );
      });

      try {
        const pdfConfig = buildOrderPdfConfig(
          async (_, bulkData) => {
            return this.getPdfData(bulkData);
          },
          isShowAllColumns,
          {
            data: sortBy(selectedItems, 'internal_number'),
            exportFileName: (): string => {
              const isSingleFile = isEqual(selectedItems.length, 1);

              const selectedRecord = first(selectedItems);
              const fileNameTruncationText = get(
                selectedRecord,
                'vendor_name',
                '',
              );
              const fileNameSuffix = get(
                selectedRecord,
                'transaction_date',
                '',
              );
              const status = get(
                selectedRecord,
                'status',
                ORDER_STATUS_VALUE.Open,
              );

              return isSingleFile
                ? getSingleExportFileName(
                    this.orderPdfService.getHeaderText(status),
                    fileNameTruncationText,
                    fileNameSuffix,
                  )
                : 'Order&Delivery Docket.pdf';
            },
          },
          true,
        );

        if (isExport) {
          await this.pdfGenerateServiceV2.downloadPdfAsync(pdfConfig);

          return;
        }

        await this.pdfGenerateServiceV2.printPdfAsync(pdfConfig);
      } catch (error) {
        this.uiActions.disableLoader();
        this.uiActions.showToasterMessage({
          type: NotificationType.ERROR,
          title: TOASTER_ERROR_MESSAGE.title,
          body: I18n.ErrorMessages.CheckInternetAndTryAgain,
        });
      }
    };
  };
}
